import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from 'react';
import { Client as ConversationsClient } from '@twilio/conversations';
import axiosInstance from '../library/helpers/axiosConfig';

const TwilioContext = createContext(undefined);

export const TwilioProvider = ({ children }) => {
  const [client, setClient] = useState(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(null);
  const [conversations, setConversations] = useState(new Map());

  const clientRef = useRef(null);
  const conversationsMapRef = useRef(new Map());
  const currentUserIdRef = useRef(null);
  const initSessionIdRef = useRef(0);
  const initializingUserIdRef = useRef(null);

  // Background warm-up for subscribed conversations to reduce the chance that an existing
  // conversation lives on a later paginator page (which could otherwise lead to creating a new
  // conversation and "splitting" history).
  //
  // This is intentionally time-boxed + page-capped to avoid long blocking work on login.
  const warmUpSubscribedConversationsInBackground = useCallback((twilioClient, firstPaginator) => {
    (async () => {
      if (!twilioClient || !firstPaginator) return;

      const MAX_PAGES = 10; // extra pages after the first one
      const MAX_MS = 8000; // time budget
      const start = Date.now();

      let paginator = firstPaginator;
      let pagesFetched = 0;

      while (paginator?.hasNextPage) {
        if (pagesFetched >= MAX_PAGES) break;
        if (Date.now() - start > MAX_MS) break;

        paginator = await paginator.nextPage();
        pagesFetched += 1;

        const items = paginator?.items || [];
        for (const conv of items) {
          try {
            if (conv?.uniqueName) {
              conversationsMapRef.current.set(conv.uniqueName, conv);
            }
            if (conv?.sid) {
              conversationsMapRef.current.set(conv.sid, conv);
            }
          } catch (e) {
            // ignore individual conversation cache errors
          }
        }

        // Stop if user switched/logged out
        if (clientRef.current !== twilioClient) return;

        // Update state occasionally (not on every page)
        if (pagesFetched % 2 === 0) {
          setConversations(new Map(conversationsMapRef.current));
        }

        // Small yield to keep UI responsive
        await new Promise((r) => setTimeout(r, 25));
      }

      // Final sync
      if (clientRef.current === twilioClient) {
        setConversations(new Map(conversationsMapRef.current));
      }
    })().catch(() => {
      // Warm-up is best-effort
    });
  }, []);

  // Get Twilio token from backend
  const getToken = useCallback(async (userId) => {
    try {
      const response = await axiosInstance.get(`api/v1/twilio/token/${userId}`);
      if (!response?.data?.token) {
        throw new Error('Token not received from server');
      }
      return response.data.token;
    } catch (error) {
      throw error;
    }
  }, []);

  // Shutdown client (on logout)
  const shutdown = useCallback(async () => {
    initSessionIdRef.current += 1;
    initializingUserIdRef.current = null;
    if (clientRef.current) {
      try {
        await clientRef.current.shutdown();
      } catch (err) {
        // Shutdown error - silently handle
      }
      clientRef.current = null;
      currentUserIdRef.current = null;
      conversationsMapRef.current.clear();
      setClient(null);
      setIsReady(false);
      setIsInitializing(false);
      setConversations(new Map());
    }
  }, []);

  // Initialize Twilio client (once per user session)
  const initializeForUser = useCallback(
    async (userId) => {
      // If already initialized and connected for this user, skip
      if (
        clientRef.current &&
        currentUserIdRef.current === userId &&
        clientRef.current.connectionState === 'connected'
      ) {
        return;
      }

      // If client is already connected for a different user, shutdown previous client first
      if (clientRef.current && currentUserIdRef.current !== userId) {
        await shutdown();
      }

      // If already actively initializing for this exact same user, don't restart; wait
      if (initializingUserIdRef.current === userId) {
        return;
      }

      const initId = ++initSessionIdRef.current;
      initializingUserIdRef.current = userId;
      setIsInitializing(true);
      setError(null);

      let newClient = null;

      try {
        // Get token
        const token = await getToken(userId);
        if (initSessionIdRef.current !== initId) {
          return;
        }

        // Create client
        newClient = new ConversationsClient(token);

        // Set up token refresh handlers
        newClient.on('tokenAboutToExpire', async () => {
          try {
            const newToken = await getToken(userId);
            if (newClient) {
              await newClient.updateToken(newToken);
            }
          } catch (err) {
            // Token refresh failed - silently handle
          }
        });

        newClient.on('tokenExpired', async () => {
          try {
            const newToken = await getToken(userId);
            if (newClient) {
              await newClient.updateToken(newToken);
            }
          } catch (err) {
            // Token refresh failed - silently handle
          }
        });

        // Wait for client initialization
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Client initialization timeout'));
          }, 30000);

          newClient.on('initialized', () => {
            clearTimeout(timeout);
            resolve();
          });

          newClient.on('connectionStateChanged', (state) => {
            if (state === 'disconnected') {
              clearTimeout(timeout);
              reject(new Error('Client disconnected during initialization'));
            }
          });
        });

        if (initSessionIdRef.current !== initId) {
          try {
            await newClient.shutdown();
          } catch (e) {}
          return;
        }

        // Set up conversation sync (per Twilio best practices)
        newClient.on('conversationAdded', (conversation) => {
          if (initSessionIdRef.current !== initId) return;
          if (conversation.uniqueName) {
            conversationsMapRef.current.set(conversation.uniqueName, conversation);
          }
          conversationsMapRef.current.set(conversation.sid, conversation);
          setConversations(new Map(conversationsMapRef.current));
        });

        newClient.on('conversationRemoved', (conversation) => {
          if (initSessionIdRef.current !== initId) return;
          if (conversation.uniqueName) {
            conversationsMapRef.current.delete(conversation.uniqueName);
          }
          conversationsMapRef.current.delete(conversation.sid);
          setConversations(new Map(conversationsMapRef.current));
        });

        // Load subscribed conversations (as per Twilio docs)
        const subscribedConversations =
          await newClient.getSubscribedConversations();

        if (initSessionIdRef.current !== initId) {
          try {
            await newClient.shutdown();
          } catch (e) {}
          return;
        }

        // Cache all subscribed conversations
        subscribedConversations.items.forEach((conv) => {
          if (conv.uniqueName) {
            conversationsMapRef.current.set(conv.uniqueName, conv);
          }
          conversationsMapRef.current.set(conv.sid, conv);
        });

        setConversations(new Map(conversationsMapRef.current));
        // Continue paging in background (time-boxed) so we don't miss older conversations.
        warmUpSubscribedConversationsInBackground(newClient, subscribedConversations);

        clientRef.current = newClient;
        currentUserIdRef.current = userId;
        initializingUserIdRef.current = null;
        setClient(newClient);
        setIsReady(true);
        setIsInitializing(false);
      } catch (err) {
        if (newClient) {
          try {
            await newClient.shutdown();
          } catch (e) {}
        }
        if (initSessionIdRef.current === initId) {
          initializingUserIdRef.current = null;
          conversationsMapRef.current.clear();
          setConversations(new Map());
          setError(err.message || 'Failed to initialize Twilio client');
          setIsInitializing(false);
          setIsReady(false);
        }
      }
    },
    [getToken, isInitializing, warmUpSubscribedConversationsInBackground, shutdown]
  );

  // Get conversation (from cache if available)
  const getConversation = useCallback(
    async (uniqueName, conversationSid) => {
      // Check cache first
      if (uniqueName && conversationsMapRef.current.has(uniqueName)) {
        return conversationsMapRef.current.get(uniqueName);
      }

      if (conversationsMapRef.current.has(conversationSid)) {
        return conversationsMapRef.current.get(conversationSid);
      }

      // Not in cache, fetch from Twilio
      if (!clientRef.current) {
        throw new Error('Twilio client not initialized');
      }

      try {
        let conversation;
        if (uniqueName) {
          conversation = await clientRef.current.getConversationByUniqueName(
            uniqueName
          );
        } else {
          conversation = await clientRef.current.getConversationBySid(
            conversationSid
          );
        }

        // Cache it
        if (conversation.uniqueName) {
          conversationsMapRef.current.set(conversation.uniqueName, conversation);
        }
        conversationsMapRef.current.set(conversation.sid, conversation);
        setConversations(new Map(conversationsMapRef.current));

        return conversation;
      } catch (err) {
        throw err;
      }
    },
    []
  );

  // Find conversation by participants (staff/user ID) - FRONTEND ONLY, NO BACKEND CALL!
  const findConversationByParticipants = useCallback(
    (staffId, userId, preferredConversationSid) => {
      // Search through already-loaded Twilio conversations
      // Search through all cached conversations
      const matchingConversations = [];

      const seenSids = new Set(); // Track seen SIDs to avoid duplicates

      // Search through all cached conversations
      for (const [_key, conversation] of conversationsMapRef.current) {
        try {
          // Skip if we've already seen this conversation SID (avoid duplicates)
          if (seenSids.has(conversation.sid)) {
            continue;
          }

          // Parse conversation attributes
          const attributes =
            conversation.attributes && typeof conversation.attributes === 'string'
              ? JSON.parse(conversation.attributes)
              : conversation.attributes || {};

          // Check if this conversation matches the staff/user pair (either direction)
          // Using == for loose comparison (handles string/number mismatch)
          const matches =
            (attributes.staffId == staffId && attributes.userId == userId) ||
            (attributes.staffId == userId && attributes.userId == staffId);

          if (matches) {
            seenSids.add(conversation.sid); // Mark as seen

            // Extract sessionUuid from uniqueName: chat_{sessionUuid}_{timestamp}_{hash}
            let sessionUuid = null;
            let timestamp = 0;
            if (conversation.uniqueName) {
              const parts = conversation.uniqueName.split('_');
              if (parts.length >= 2) {
                sessionUuid = parts[1];
              }
              // Extract timestamp from uniqueName (3rd part: chat_{uuid}_{timestamp}_{hash})
              if (parts.length >= 3) {
                timestamp = parseInt(parts[2], 10) || 0;
              }
            }

            // Prefer dateUpdated (last activity) over dateCreated (creation time)
            const dateUpdated = conversation.dateUpdated
              ? new Date(conversation.dateUpdated).getTime()
              : 0;
            const dateCreated = conversation.dateCreated
              ? new Date(conversation.dateCreated).getTime()
              : 0;

            // Use dateUpdated if available (shows last activity), otherwise dateCreated, otherwise timestamp from uniqueName
            const sortTimestamp = dateUpdated || dateCreated || timestamp;

            // Check if conversation has messages (lastMessage indicates activity)
            const hasMessages = !!conversation.lastMessage;
            const lastMessageTimestamp = conversation.lastMessage?.dateCreated
              ? new Date(conversation.lastMessage.dateCreated).getTime()
              : 0;

            const isPreferred =
              preferredConversationSid &&
              conversation.sid === preferredConversationSid;

            matchingConversations.push({
              conversation,
              conversationSid: conversation.sid,
              uniqueName: conversation.uniqueName,
              sessionUuid,
              isPreferred: !!isPreferred,
              timestamp: sortTimestamp,
              lastMessageTimestamp,
              hasMessages,
            });
          }
        } catch (parseError) {
          // Skip conversations with invalid attributes
          continue;
        }
      }

      // If found in already-loaded conversations, return immediately (fastest path!)
      if (matchingConversations.length > 0) {
        // If we have a preferred conversation SID (from backend), use that
        const preferredMatch = matchingConversations.find((m) => m.isPreferred);
        if (preferredMatch) {
          return {
            found: true,
            conversation: preferredMatch.conversation,
            conversationSid: preferredMatch.conversationSid,
            uniqueName: preferredMatch.uniqueName,
            sessionUuid: preferredMatch.sessionUuid,
            isExisting: true,
          };
        }

        // If multiple matches, sort by timestamp (most recent first) and pick the best one
        if (matchingConversations.length > 1) {
          // Sort by lastMessageTimestamp (descending - most recent first)
          // Prioritize conversations that have messages
          matchingConversations.sort((a, b) => {
            // If one has messages and the other doesn't, prioritize the one with messages
            if (a.hasMessages && !b.hasMessages) return -1;
            if (!a.hasMessages && b.hasMessages) return 1;

            // If both have messages or neither has messages, sort by their respective timestamps
            const timestampA = a.lastMessageTimestamp || a.timestamp;
            const timestampB = b.lastMessageTimestamp || b.timestamp;
            return timestampB - timestampA;
          });
        }

        // Pick the most recent conversation (first after sorting)
        const bestMatch = matchingConversations[0];

        return {
          found: true,
          conversation: bestMatch.conversation,
          conversationSid: bestMatch.conversationSid,
          uniqueName: bestMatch.uniqueName,
          sessionUuid: bestMatch.sessionUuid,
          isExisting: true,
        };
      }

      // If found in already-loaded conversations, return it
      if (matchingConversations.length > 0) {
        // If we have a preferred conversation SID (from backend), use that
        const preferredMatch = matchingConversations.find((m) => m.isPreferred);
        if (preferredMatch) {
          return {
            found: true,
            conversation: preferredMatch.conversation,
            conversationSid: preferredMatch.conversationSid,
            uniqueName: preferredMatch.uniqueName,
            sessionUuid: preferredMatch.sessionUuid,
            isExisting: true,
          };
        }

        // If multiple matches, sort by timestamp (most recent first) and pick the best one
        if (matchingConversations.length > 1) {
          // Sort by lastMessageTimestamp (descending - most recent first)
          // Prioritize conversations that have messages
          matchingConversations.sort((a, b) => {
            // If one has messages and the other doesn't, prioritize the one with messages
            if (a.hasMessages && !b.hasMessages) return -1;
            if (!a.hasMessages && b.hasMessages) return 1;

            // If both have messages or neither has messages, sort by their respective timestamps
            const timestampA = a.lastMessageTimestamp || a.timestamp;
            const timestampB = b.lastMessageTimestamp || b.timestamp;
            return timestampB - timestampA;
          });
        }

        // Pick the most recent conversation (first after sorting)
        const bestMatch = matchingConversations[0];

        return {
          found: true,
          conversation: bestMatch.conversation,
          conversationSid: bestMatch.conversationSid,
          uniqueName: bestMatch.uniqueName,
          sessionUuid: bestMatch.sessionUuid,
          isExisting: true,
        };
      }

      // Not found in any cache - return not found
      // The caller should create a new session
      return { found: false };
    },
    []
  );

  // Mark all conversations for a user as read (handles multiple conversations)
  // Uses the SAME matching strategies as Sidebar to ensure consistency
  const markAllConversationsAsRead = useCallback(async (staffId, userId) => {
    if (!clientRef.current) {
      console.warn('[Twilio] markAllConversationsAsRead: No Twilio client available');
      return;
    }

    const conversationsToMark = new Set();
    const seenConversationSids = new Set();

    if (conversationsMapRef.current && conversationsMapRef.current.size > 0) {
      for (const [, conv] of conversationsMapRef.current) {
        if (!conv) continue;

        let attributes = {};
        try {
          attributes = typeof conv.attributes === 'string'
            ? JSON.parse(conv.attributes)
            : conv.attributes || {};
        } catch (e) {
          attributes = {};
        }

        const matches =
          (attributes.staffId == staffId && attributes.userId == userId) ||
          (attributes.staffId == userId && attributes.userId == staffId);

        if (matches) {
          if (conv?.sid && !seenConversationSids.has(conv.sid)) {
            seenConversationSids.add(conv.sid);
            conversationsToMark.add(conv);
          }
        }
      }
    }

    for (const conv of conversationsToMark) {
      if (!conv) continue;
      try {
        if (conv.status !== "joined") {
          await conv.join();
        }

        const messages = await conv.getMessages(1);
        const items = messages?.items || [];

        if (items.length > 0) {
          const lastIndex = items[items.length - 1].index;
          await conv.updateLastReadMessageIndex(lastIndex);
        }
      } catch (e) {
        console.warn('[Twilio] Failed to mark conversation as read:', e);
      }
    }
  }, []);


  const value = {
    client,
    isInitializing,
    isReady,
    error,
    conversations,
    initializeForUser,
    shutdown,
    getConversation,
    findConversationByParticipants,
    markAllConversationsAsRead,
  };

  return (
    <TwilioContext.Provider value={value}>{children}</TwilioContext.Provider>
  );
};

export const useTwilio = () => {
  const context = useContext(TwilioContext);
  if (context === undefined) {
    throw new Error('useTwilio must be used within a TwilioProvider');
  }
  return context;
};

export default TwilioContext;

